/**
 * Timeline View Component
 * Main component for on-call timeline visualization
 * Header and ViewData are separated to optimize re-renders
 */

import React, { useState, useCallback } from 'react'

import { useModal } from '@/contexts/modal'

import { TimelineDateRange, WeekRangeOption } from '../../../types/timeline'

import TimelineHeader from './components/TimelineHeader'
import TimelineViewData from './components/TimelineViewData'

import { navigateDateRange, getDateRangeForOption } from './utils/dateUtils'

interface TimelineViewProps {
    scheduleId?: number
    scheduleName?: string
    showActions?: boolean
    defaultWeekRange?: WeekRangeOption
    className?: string
}

const TimelineView: React.FC<TimelineViewProps> = ({
    scheduleId,
    scheduleName,
    showActions = true,
    defaultWeekRange = '1_week',
    className,
}) => {
    const { setModal } = useModal()

    // State for date range navigation
    const [dateRange, setDateRange] = useState<TimelineDateRange>(() =>
        getDateRangeForOption(defaultWeekRange)
    )
    const [weekRange, setWeekRange] =
        useState<WeekRangeOption>(defaultWeekRange)

    // Navigation handlers
    const handleNavigate = useCallback(
        (direction: 'prev' | 'next') => {
            setDateRange((current) =>
                navigateDateRange(current, direction, weekRange)
            )
        },
        [weekRange]
    )

    const handleWeekRangeChange = useCallback((range: WeekRangeOption) => {
        setWeekRange(range)
        setDateRange(getDateRangeForOption(range))
    }, [])

    const handleDateRangeChange = useCallback((range: TimelineDateRange) => {
        setDateRange(range)
    }, [])

    // Action handlers
    const handleTakeOncall = useCallback(() => {
        setModal('Oncall/TakeOncallModal/index', {
            scheduleId,
            onConfirm: () => {
                // Will use existing swap/reassignment functionality
            },
        })
    }, [setModal, scheduleId])

    const handleGenerateAssignments = useCallback(() => {
        setModal('Oncall/GenerateAssignmentsModal/index', {
            scheduleId,
            refetch: () => {
                // The query will auto-refetch due to cache invalidation
            },
        })
    }, [setModal, scheduleId])

    return (
        <div className={`bg-white rounded-md p-4 ${className || ''}`}>
            <TimelineHeader
                dateRange={dateRange}
                weekRange={weekRange}
                onDateRangeChange={handleDateRangeChange}
                onWeekRangeChange={handleWeekRangeChange}
                onNavigate={handleNavigate}
                onTakeOncall={showActions ? handleTakeOncall : undefined}
                onGenerateAssignments={
                    showActions ? handleGenerateAssignments : undefined
                }
                scheduleId={scheduleId}
                scheduleName={scheduleName}
            />

            <TimelineViewData scheduleId={scheduleId} dateRange={dateRange} />
        </div>
    )
}

export default TimelineView
